import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import type { CoachSportQualificationFormData } from '@court-sync/shared';
import { Button, Field, FieldLabel, FieldError } from '@/components/ui';
import { useAcademySports } from '@/queries/academySports';
import { coachService } from '@/services/coach';
import { getAcademySportLabel } from '@/utils/sportPresentation';

const LEVEL_OPTIONS: Array<{ value: CoachSportQualificationFormData['level']; label: string }> = [
  { value: 'assistant', label: 'Assistant' },
  { value: 'certified', label: 'Certified' },
  { value: 'head', label: 'Head coach' },
  { value: 'pro', label: 'Pro / high performance' },
];

export interface CoachSportQualificationsFieldProps {
  academyId: string;
  coachId?: string;
  disabled?: boolean;
  onSaved?: () => void;
}

export function CoachSportQualificationsField({
  academyId,
  coachId,
  disabled = false,
  onSaved,
}: CoachSportQualificationsFieldProps) {
  const { sports, isLoading: sportsLoading } = useAcademySports(academyId);
  const activeSports = sports.filter((s) => s.isActive);
  const [rows, setRows] = useState<CoachSportQualificationFormData[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadedCoachId, setLoadedCoachId] = useState<string | null>(null);

  useEffect(() => {
    if (!coachId) {
      setRows([]);
      setLoadedCoachId(null);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const existing = await coachService.getCoachSportQualifications(coachId, academyId);
        if (cancelled) return;
        setRows(
          existing.map((q) => ({
            academySportId: q.academySportId,
            level: q.level,
            certifiedAt: q.certifiedAt ?? null,
            notes: q.notes ?? null,
          }))
        );
        setLoadedCoachId(coachId);
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          toast.error('Failed to load sport qualifications');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [coachId, academyId]);

  const toggleSport = (academySportId: string) => {
    setRows((prev) => {
      const exists = prev.find((r) => r.academySportId === academySportId);
      if (exists) {
        return prev.filter((r) => r.academySportId !== academySportId);
      }
      return [...prev, { academySportId, level: 'certified', certifiedAt: null, notes: null }];
    });
  };

  const updateRow = (
    academySportId: string,
    patch: Partial<CoachSportQualificationFormData>
  ) => {
    setRows((prev) =>
      prev.map((r) => (r.academySportId === academySportId ? { ...r, ...patch } : r))
    );
  };

  const handleSave = async () => {
    if (!coachId) return;
    setSaving(true);
    try {
      await coachService.updateCoachSportQualifications(coachId, academyId, rows);
      toast.success('Sport qualifications saved');
      onSaved?.();
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Failed to save qualifications');
    } finally {
      setSaving(false);
    }
  };

  if (!coachId) {
    return (
      <p className="text-sm text-muted-foreground">
        Save the coach first, then assign sport qualifications.
      </p>
    );
  }

  if (sportsLoading || loading) {
    return <p className="text-sm text-muted-foreground">Loading sport qualifications…</p>;
  }

  if (activeSports.length === 0) {
    return (
      <p className="text-sm text-amber-700 dark:text-amber-300">
        Add sports in Academy Settings before qualifying coaches.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <FieldLabel>Sport qualifications</FieldLabel>
        <p className="text-xs text-muted-foreground mt-1 mb-3">
          Select sports this coach is qualified to teach at your academy.
        </p>
        <div className="space-y-3">
          {activeSports.map((sport) => {
            const row = rows.find((r) => r.academySportId === sport.id);
            const checked = !!row;
            return (
              <div
                key={sport.id}
                className="rounded-lg border border-border p-3 space-y-2"
              >
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggleSport(sport.id)}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm font-medium">{getAcademySportLabel(sport)}</span>
                </label>
                {checked && row ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-6">
                    <Field>
                      <FieldLabel htmlFor={`coach-sport-level-${sport.id}`}>Level</FieldLabel>
                      <select
                        id={`coach-sport-level-${sport.id}`}
                        className="input w-full"
                        disabled={disabled}
                        value={row.level}
                        onChange={(e) =>
                          updateRow(sport.id, {
                            level: e.target.value as CoachSportQualificationFormData['level'],
                          })
                        }
                      >
                        {LEVEL_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field>
                      <FieldLabel htmlFor={`coach-sport-certified-${sport.id}`}>
                        Certified (optional)
                      </FieldLabel>
                      <input
                        id={`coach-sport-certified-${sport.id}`}
                        type="date"
                        className="input w-full"
                        disabled={disabled}
                        value={row.certifiedAt?.slice(0, 10) ?? ''}
                        onChange={(e) =>
                          updateRow(sport.id, {
                            certifiedAt: e.target.value ? e.target.value : null,
                          })
                        }
                      />
                    </Field>
                    <Field className="sm:col-span-2">
                      <FieldLabel htmlFor={`coach-sport-notes-${sport.id}`}>Notes</FieldLabel>
                      <textarea
                        id={`coach-sport-notes-${sport.id}`}
                        rows={2}
                        className="input w-full"
                        disabled={disabled}
                        value={row.notes ?? ''}
                        onChange={(e) => updateRow(sport.id, { notes: e.target.value || null })}
                        placeholder="e.g. PTR certified, club pro badge…"
                      />
                    </Field>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        <FieldError />
      </div>
      {loadedCoachId === coachId ? (
        <div className="flex justify-end">
          <Button type="button" size="sm" onClick={() => void handleSave()} disabled={disabled || saving}>
            {saving ? 'Saving…' : 'Save qualifications'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
